import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import toast from 'react-hot-toast';
import { X, Users, Calendar, Clock, MapPin, UserPlus, UserMinus, CheckCircle, XCircle, RefreshCw } from 'lucide-react';
import SessionWaitlistSection from './SessionWaitlistSection';

export default function CourseSessionDetailModal({ isOpen, session, onClose, onSessionUpdated }) {
    const { getAuthenticatedApi, isGym } = useAuth();
    
    const [loading, setLoading] = useState(false);
    const [sessionDetails, setSessionDetails] = useState(null);
    const [allUsers, setAllUsers] = useState([]);
    const [showEnrollModal, setShowEnrollModal] = useState(false);
    const [selectedUsers, setSelectedUsers] = useState([]);

    useEffect(() => {
        if (isOpen && session) {
            fetchSessionDetails();
            fetchUsers();
        }
    }, [isOpen, session]);

    const fetchSessionDetails = async () => {
        try {
            setLoading(true);
            const api = getAuthenticatedApi();
            const response = await api.get(`/gym_courses.php?action=get_session&id=${session.id}`);
            setSessionDetails(response.data.session);
        } catch (err) {
            console.error('Errore caricamento dettagli:', err);
        } finally {
            setLoading(false);
        }
    };

    const fetchUsers = async () => {
        try {
            const api = getAuthenticatedApi();
            const response = await api.get('/users.php?role_name=user');
            setAllUsers(response.data || []);
        } catch (err) {
            console.error('Errore caricamento utenti:', err);
        }
    };

    const handleEnrollUsers = async () => {
        if (selectedUsers.length === 0) return;

        try {
            const api = getAuthenticatedApi();
            await api.post('/gym_courses.php?action=enroll_users', {
                session_id: session.id,
                user_ids: selectedUsers,
                send_notification: true
            });
            
            toast.success(`✅ ${selectedUsers.length} utenti iscritti con successo!`);
            setShowEnrollModal(false);
            setSelectedUsers([]);
            fetchSessionDetails();
            onSessionUpdated();
            
        } catch (err) {
            console.error('Errore iscrizione:', err);
            toast.error('Errore durante l\'iscrizione');
        }
    };

    const handleCancelEnrollment = async (enrollmentId) => {
        if (!window.confirm('Sei sicuro di voler annullare questa iscrizione?')) return;

        try {
            const api = getAuthenticatedApi();
            await api.post('/gym_courses.php?action=cancel_enrollment', {
                enrollment_id: enrollmentId,
                send_notification: true
            });
            
            toast.success('Iscrizione annullata con successo');
            fetchSessionDetails();
            onSessionUpdated();
            
        } catch (err) {
            console.error('Errore annullamento:', err);
            toast.error('Errore durante l\'annullamento');
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
            <div className="bg-white dark:bg-gray-900 rounded-lg w-full max-w-3xl my-8">
                {/* Header: logo corso da sessionDetails dopo fetch (WEBAPP-027) */}
                <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-white/10">
                    <div className="flex items-center gap-3 min-w-0">
                        {(sessionDetails?.course_logo_filename || session?.course_logo_filename) ? (
                            <img
                                src={`/api/serve_image.php?filename=${encodeURIComponent(sessionDetails?.course_logo_filename || session?.course_logo_filename)}&type=logo`}
                                alt={sessionDetails?.course_title || session?.course_title || ''}
                                className="w-10 h-10 object-cover rounded-lg border border-gray-200 dark:border-gray-700 flex-shrink-0"
                            />
                        ) : (
                            <div className="flex-shrink-0">
                                <Calendar size={24} className="text-gray-500 dark:text-white/65" />
                            </div>
                        )}
                        <div className="min-w-0">
                            <h3 className="text-xl font-semibold text-gray-900 dark:text-white truncate">
                                {sessionDetails?.course_title ?? session?.title ?? session?.course_title}
                            </h3>
                            <p className="text-sm text-gray-500 dark:text-white/65 mt-1">
                                Sessione Corso
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                    >
                        <X size={24} />
                    </button>
                </div>

                <div className="p-6">
                    {loading ? (
                        <div className="flex justify-center py-8">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                        </div>
                    ) : sessionDetails ? (
                        <div>
                            {/* Info Sessione */}
                            <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 mb-6">
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                    <div>
                                        <p className="text-sm text-gray-500 dark:text-white/65">Data</p>
                                        <p className="font-medium text-gray-900 dark:text-white">
                                            {new Date(sessionDetails.session_date).toLocaleDateString('it-IT')}
                                        </p>
                                    </div>
                                    <div>
                                        <p className="text-sm text-gray-500 dark:text-white/65">Orario</p>
                                        <p className="font-medium text-gray-900 dark:text-white">
                                            {sessionDetails.start_time?.slice(0, 5)} - {sessionDetails.end_time?.slice(0, 5)}
                                        </p>
                                    </div>
                                    <div>
                                        <p className="text-sm text-gray-500 dark:text-white/65">Partecipanti</p>
                                        <p className="font-medium text-gray-900 dark:text-white">
                                            {sessionDetails.max_participants !== null && sessionDetails.max_participants !== undefined && sessionDetails.max_participants > 0
                                                ? `${sessionDetails.current_participants || 0} / ${sessionDetails.max_participants}`
                                                : `${sessionDetails.current_participants || 0} iscritti`
                                            }
                                        </p>
                                    </div>
                                    {sessionDetails.location && (
                                        <div>
                                            <p className="text-sm text-gray-500 dark:text-white/65">Location</p>
                                            <p className="font-medium text-gray-900 dark:text-white">
                                                {sessionDetails.location}
                                            </p>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Lista Iscritti */}
                            {(() => {
                                // Filtra utenti con status 'cancelled' (non devono essere mostrati)
                                const activeEnrollments = (sessionDetails.enrollments || []).filter(
                                    e => e.status !== 'cancelled'
                                );
                                return (
                                    <>
                                        <div className="flex justify-between items-center mb-4">
                                            <h4 className="text-lg font-semibold text-gray-900 dark:text-white">
                                                Iscritti ({activeEnrollments.length})
                                            </h4>
                                            {isGym() && (
                                                <button
                                                    onClick={() => setShowEnrollModal(true)}
                                                    className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-sm"
                                                >
                                                    <UserPlus size={16} />
                                                    Iscrivi Utenti
                                                </button>
                                            )}
                                        </div>

                                        {activeEnrollments.length > 0 ? (
                                            <div className="space-y-2 max-h-[400px] overflow-y-auto">
                                                {activeEnrollments.map(enrollment => (
                                        <div
                                            key={enrollment.id}
                                            className="flex items-center justify-between p-3 bg-white dark:bg-gray-900 border border-gray-200 dark:border-white/10 rounded-lg"
                                        >
                                            <div className="flex items-center gap-3">
                                                <div 
                                                    className="w-10 h-10 rounded-full flex items-center justify-center"
                                                    style={{ backgroundColor: session.color || '#3B82F6' }}
                                                >
                                                    <span className="text-white font-semibold">
                                                        {enrollment.user_name?.charAt(0).toUpperCase()}
                                                    </span>
                                                </div>
                                                <div>
                                                    <p className="font-medium text-gray-900 dark:text-white">
                                                        {enrollment.user_name}
                                                    </p>
                                                    <p className="text-sm text-gray-500 dark:text-white/65">
                                                        {enrollment.user_email}
                                                    </p>
                                                </div>
                                                {enrollment.status === 'attended' && (
                                                    <CheckCircle className="text-green-500" size={20} />
                                                )}
                                                {enrollment.status === 'absent' && (
                                                    <XCircle className="text-red-500" size={20} />
                                                )}
                                            </div>
                                            {enrollment.status === 'enrolled' && isGym() && (
                                                <button
                                                    onClick={() => handleCancelEnrollment(enrollment.id)}
                                                    className="text-red-600 hover:text-red-700 p-2 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20"
                                                    title="Annulla iscrizione"
                                                >
                                                    <UserMinus size={18} />
                                                </button>
                                            )}
                                                    </div>
                                                ))}
                                            </div>
                                        ) : (
                                            <p className="text-gray-500 dark:text-white/65 text-center py-8">
                                                Nessun utente iscritto
                                            </p>
                                        )}
                                    </>
                                );
                            })()}
                            <SessionWaitlistSection
                                session={sessionDetails}
                                users={allUsers}
                                onChanged={() => {
                                    fetchSessionDetails();
                                    onSessionUpdated();
                                }}
                            />
                        </div>
                    ) : (
                        <div className="flex justify-center py-8">
                            <RefreshCw className="animate-spin text-indigo-600" size={24} />
                        </div>
                    )}
                </div>

                {/* Modal Iscrizione Utenti */}
                {showEnrollModal && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                        <div className="bg-white dark:bg-gray-900 rounded-lg p-6 max-w-md w-full mx-4">
                            <div className="flex items-center gap-3 mb-4">
                                {(sessionDetails?.course_logo_filename || session?.course_logo_filename) && (
                                    <img
                                        src={`/api/serve_image.php?filename=${encodeURIComponent(sessionDetails?.course_logo_filename || session?.course_logo_filename)}&type=logo`}
                                        alt={sessionDetails?.course_title || session?.course_title || ''}
                                        className="w-10 h-10 object-cover rounded-lg border border-gray-200 dark:border-gray-700 flex-shrink-0"
                                    />
                                )}
                                <div className="min-w-0">
                                    <h4 className="text-lg font-semibold text-gray-900 dark:text-white">
                                        Seleziona Utenti da Iscrivere
                                    </h4>
                                    <p className="text-sm text-gray-500 dark:text-white/65 truncate">
                                        {sessionDetails?.course_title ?? session?.course_title ?? ''}
                                    </p>
                                </div>
                            </div>
                            <div className="max-h-[400px] overflow-y-auto space-y-2 mb-4">
                                {allUsers
                                    .filter(user => !sessionDetails?.enrollments?.some(e => e.user_id === user.id))
                                    .map(user => (
                                    <label
                                        key={user.id}
                                        className="flex items-center gap-3 p-2 hover:bg-gray-50 dark:hover:bg-white/[0.06] rounded cursor-pointer"
                                    >
                                        <input
                                            type="checkbox"
                                            checked={selectedUsers.includes(user.id)}
                                            onChange={(e) => {
                                                if (e.target.checked) {
                                                    setSelectedUsers([...selectedUsers, user.id]);
                                                } else {
                                                    setSelectedUsers(selectedUsers.filter(id => id !== user.id));
                                                }
                                            }}
                                            className="w-4 h-4 text-indigo-600 rounded"
                                        />
                                        <span className="text-gray-900 dark:text-white">{user.name}</span>
                                    </label>
                                ))}
                            </div>
                            <div className="flex justify-end gap-3">
                                <button
                                    onClick={() => {
                                        setShowEnrollModal(false);
                                        setSelectedUsers([]);
                                    }}
                                    className="px-4 py-2 text-gray-700 dark:text-white/85 hover:bg-gray-100 dark:hover:bg-white/10 rounded-lg"
                                >
                                    Annulla
                                </button>
                                <button
                                    onClick={handleEnrollUsers}
                                    disabled={selectedUsers.length === 0}
                                    className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg disabled:opacity-50 flex items-center gap-2"
                                >
                                    <UserPlus size={16} />
                                    Iscrive ({selectedUsers.length})
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

